import { LINE_STATUSES } from '../firebase/schema';

/** Line statuses that mean blanks were placed / the job left the ordering queue. */
export const LINE_STATUSES_ORDERED_OR_LATER = [
  LINE_STATUSES.ORDERED,
  LINE_STATUSES.IN_NOT_CUT,
  LINE_STATUSES.CUT_NOT_IN,
  LINE_STATUSES.READY_TO_PRINT,
  LINE_STATUSES.PRINTED,
  LINE_STATUSES.COMPLETED
];

export function lineHasBeenOrdered(status) {
  return LINE_STATUSES_ORDERED_OR_LATER.includes(status);
}

/**
 * A line is "finalized" once it has been printed/completed. Its status must never be
 * recomputed (downgraded) by later IN/CUT or receiving changes — otherwise a finished
 * job can silently revert back onto the print list.
 */
export function lineIsFinalized(line) {
  return (
    line?.status === LINE_STATUSES.COMPLETED ||
    line?.status === LINE_STATUSES.PRINTED ||
    line?.printed === true
  );
}

/**
 * Status to set when marking a line as cut (film/screen).
 * Pre-order lines (e.g. Ready To Order) keep their status so Ordering still lists them.
 * Finalized (printed/completed) lines keep their status so they don't revert.
 */
export function lineStatusAfterCut(line) {
  const status = line?.status;
  if (lineIsFinalized(line)) {
    return status;
  }
  if (!lineHasBeenOrdered(status)) {
    return status;
  }
  return line.isIn ? LINE_STATUSES.READY_TO_PRINT : LINE_STATUSES.CUT_NOT_IN;
}

export function lineAwaitingSupplierOrder(status) {
  return status === LINE_STATUSES.READY_TO_ORDER || status === LINE_STATUSES.NEW;
}

/**
 * The status a line effectively has right now, reconciling the stored `status` field
 * with the isIn / isCut / printed booleans.
 *
 * Use this for display so a line's status label and its color always agree. Previously
 * the label was derived from the booleans while the color came from the stored status,
 * so a line that was IN + CUT but whose stored status hadn't caught up showed the words
 * "Ready to Print" in grey instead of green. This mirrors the same IN + CUT => Ready To
 * Print rule the IN/CUT toggle already applies when it writes to the database.
 */
export function getEffectiveLineStatus(line) {
  if (!line) return LINE_STATUSES.NEW;
  // Finalized lines win and must never be visually downgraded.
  if (lineIsFinalized(line)) {
    return line.status === LINE_STATUSES.COMPLETED
      ? LINE_STATUSES.COMPLETED
      : LINE_STATUSES.PRINTED;
  }
  // Blanks are here (IN) and cut => ready to print.
  if (line.isIn && line.isCut) return LINE_STATUSES.READY_TO_PRINT;
  if (line.isIn) return LINE_STATUSES.IN_NOT_CUT;
  if (line.isCut) return LINE_STATUSES.CUT_NOT_IN;
  // Otherwise trust the stored status.
  return line.status || LINE_STATUSES.NEW;
}
